import axios from "axios";
import { ref, Ref } from "vue";
import { tryOnScopeDispose } from "@vueuse/core";
import { useQueryClient, QueryClient } from "@tanstack/vue-query";
import api from "@/services/api";
import type {
  AIAgentContext,
  AIAgentMessage,
  AIAgentRunState,
  AIAgentSession,
  ChatAttachment,
  ReasoningEffort,
} from "@/services/ai_agent";

/**
 * Extract a human-readable error message from an API error.
 * Prefers the backend's `error` field from the response body,
 * falls back to Axios message, then to a generic fallback.
 */
export function getApiErrorMessage(
  error: unknown,
  fallback = "Unknown error",
): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | undefined;
    if (data && typeof data.error === "string") {
      return data.error;
    }
    if (data && typeof data.detail === "string") {
      return data.detail;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

export interface StreamState {
  isStreaming: boolean;
  currentMessage: string;
  toolCalls: {
    name: string;
    status: "running" | "complete";
    duration?: number;
  }[];
  error: string | null;
  tokensUsed: number;
  iteration: number;
}

export interface StreamResult {
  /** True when polling already pushed fresh messages into the query cache. */
  messagesAlreadyCached: boolean;
}

interface OutgoingMessage {
  message: string;
  context?: AIAgentContext;
  attachments?: ChatAttachment[];
  effort?: ReasoningEffort;
}

// Includes the consolidated tool names AND the pre-fold legacy names that
// the backend's LEGACY_TOOL_NAME_ALIASES still routes through, so stored
// sessions replaying old tool_calls still invalidate the right caches.
//
// TODO(deprecation): remove the legacy entries in lockstep with
// `LEGACY_TOOL_NAME_ALIASES` in `backend/ai_agents/tools/adapter.py`.
// See that map's TODO comment for the removal trigger (no stored
// AgentMessage references a pre-fold name in the last 90 days). The two
// surfaces are intentionally co-owned: deleting one without the other
// causes silent cache-invalidation drift on replayed sessions.

/** Tool names that modify dashboard data. */
const DASHBOARD_MODIFYING_TOOLS = new Set([
  "update_dashboard",
  "manage_dashboard_history",
  "create_dashboard",
  "create_widget_simple",
  "create_goal_or_kpi_widget",
  "manage_tab",
  "manage_widgets",
  // legacy aliases (pre-fold)
  "undo_dashboard_update",
  "create_dashboard_simple",
  "create_dashboard_auto_layout",
  "create_marketing_dashboard",
  "create_dashboard_from_template",
  "add_tab_to_dashboard",
  "remove_tab_from_dashboard",
  "add_widgets_to_tab",
  "replace_tab_widgets",
]);

/** Tool names that modify calculated (custom) metric definitions. */
const CALCULATED_METRIC_MODIFYING_TOOLS = new Set(["manage_calculated_metric"]);

/** Tool names that modify goal or KPI data. */
const GOAL_KPI_MODIFYING_TOOLS = new Set([
  "manage_goal",
  "manage_kpi",
  "create_goal_or_kpi_widget",
  // legacy aliases (pre-fold)
  "create_goal",
  "update_goal",
  "delete_goal",
  "create_kpi",
  "update_kpi",
  "delete_kpi",
]);

/**
 * Invalidate the query caches implied by the given tool names.
 *
 * Polling covers proxy chains that block SSE, so a change seen only through
 * polling must refresh the same caches.
 */
function invalidateForToolNames(
  queryClient: QueryClient,
  toolNames: Iterable<string>,
): void {
  let dashboard = false;
  let calculatedMetric = false;
  let goalKpi = false;
  for (const name of toolNames) {
    if (DASHBOARD_MODIFYING_TOOLS.has(name)) dashboard = true;
    if (CALCULATED_METRIC_MODIFYING_TOOLS.has(name)) calculatedMetric = true;
    if (GOAL_KPI_MODIFYING_TOOLS.has(name)) goalKpi = true;
  }
  if (dashboard) {
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    queryClient.invalidateQueries({ queryKey: ["dashboards"] });
    // Every one of these writes or moves through the dashboard's history,
    // and the editor's undo and redo buttons read their state from it.
    queryClient.invalidateQueries({ queryKey: ["dashboardRevisions"] });
  }
  if (calculatedMetric) {
    // Column pickers and widget metric selectors read this list, and
    // dashboards render the metric itself. `profile` carries
    // column_preference, which the server strips the deleted metric
    // from — same set the REST delete path invalidates.
    queryClient.invalidateQueries({ queryKey: ["calculatedMetrics"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    queryClient.invalidateQueries({ queryKey: ["profile"] });
  }
  if (goalKpi) {
    queryClient.invalidateQueries({ queryKey: ["goals"] });
    queryClient.invalidateQueries({ queryKey: ["kpis"] });
    queryClient.invalidateQueries({ queryKey: ["kpi"] });
    queryClient.invalidateQueries({ queryKey: ["kpiCalculation"] });
    queryClient.invalidateQueries({ queryKey: ["kpiTimeSeries"] });
  }
}

/**
 * Whether a run may still save a change: the turn's own, or a stopped one
 * finishing a tool. Null when the cache can't say.
 */
function runMayStillSave(
  state?: Pick<AIAgentSession, "runAlive" | "runFinishing">,
): boolean | null {
  const leases = [state?.runAlive, state?.runFinishing];
  if (leases.includes(true)) return true;
  // A server from before the lease sends no runAlive.
  if (leases.includes(null) || state?.runAlive === undefined) return null;
  return false;
}

interface TurnToolCall {
  id: string;
  name: string;
  done: boolean;
}

// A result answers the latest earlier assistant message still waiting on its
// id, wherever it was saved: parallel calls finish out of order, a stopped
// turn's tool can save its result past the next turn's calls, and chats saved
// before ids were unique per session repeat them across responses.
function turnToolCalls(turn: AIAgentMessage[]): TurnToolCall[] {
  const blocks: { msg: AIAgentMessage; answered: Set<string> }[] = [];
  for (const msg of turn) {
    if (msg.role === "assistant" && msg.toolCalls?.length) {
      blocks.push({ msg, answered: new Set() });
    } else if (msg.role === "tool" && msg.toolCallId) {
      const id = msg.toolCallId;
      for (let b = blocks.length - 1; b >= 0; b--) {
        const { msg: call, answered } = blocks[b];
        if (!answered.has(id) && call.toolCalls?.some((c) => c.id === id)) {
          answered.add(id);
          break;
        }
      }
    }
  }
  return blocks.flatMap(({ msg, answered }) =>
    (msg.toolCalls ?? []).map((call) => ({
      id: call.id,
      name: call.name,
      done: answered.has(call.id),
    })),
  );
}

interface TrackedTurn {
  sessionId: number;
  /**
   * Tool calls after this answer are followed. A turn that takes over a
   * Stop's follow-up keeps the stopped turn's, whose tool may still be
   * running.
   */
  since: number | null;
  // By call id, which the server keeps unique per session: the stream,
  // polling and the after-Stop follow-up each refresh a call only once.
  refreshedToolCalls: Set<string>;
}

function refreshFinishedToolCalls(
  queryClient: QueryClient,
  turn: TrackedTurn,
  calls: TurnToolCall[],
): number {
  const newlyDone = calls.filter(
    (call) => call.done && !turn.refreshedToolCalls.has(call.id),
  );
  for (const call of newlyDone) turn.refreshedToolCalls.add(call.id);
  if (newlyDone.length > 0) {
    invalidateForToolNames(
      queryClient,
      newlyDone.map((call) => call.name),
    );
  }
  return calls.filter((call) => !call.done).length;
}

function turnAfter(
  messages: AIAgentMessage[],
  lastFinalId: number | null,
): AIAgentMessage[] {
  const idx =
    lastFinalId === null ? -1 : messages.findIndex((m) => m.id === lastFinalId);
  return messages.slice(idx + 1);
}

// Polling runs alongside SSE so responses are picked up even when the
// proxy chain blocks the EventSource stream.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_DURATION_MS = 20 * 60 * 1000; // 20 minutes
// SSE events prove the run alive whatever its lease says, but not for long:
// a proxy can hold the client's side of a stream open after the run died.
const SSE_PROOF_OF_LIFE_MS = 60_000;
// Polls in a row that must miss the run's lease before the run counts as
// gone: a healthy run loses it until its next renewal when the cache
// restarts or is flushed.
const LEASE_MISSES_BEFORE_GONE = 3;
// Unknown-lease reads in a row that end a Stop's follow-up even with a tool
// still running: a run that died during a cache outage never saves its result.
const UNKNOWN_LEASE_FOLLOW_MAX = 20;
// How long an unmounted panel's stream may wait for the run to say it has the
// message, which a buffering proxy can hold back until the turn ends. The
// server drops an untaken message after 60 s, and a run that takes one saves
// it within seconds.
const DETACHED_STREAM_MAX_MS = 90_000;

/**
 * Return the ID of the last *final* assistant message — one that has
 * actual content and no pending tool calls.  Intermediate assistant
 * messages (with toolCalls) are saved to DB while the agent is still
 * working, so they must be skipped.
 */
function lastFinalAssistantId(messages: AIAgentMessage[]): number | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role === "assistant" && (!m.toolCalls || m.toolCalls.length === 0)) {
      return m.id;
    }
  }
  return null;
}

export const RUN_STOPPED_MESSAGE =
  "The AI agent stopped before it could answer. Please send your message again.";

class StopNotPlacedError extends Error {}

export class TurnCancelledError extends Error {
  // A stopped message goes back in the input only if this resolves true: a
  // cancel the server refused leaves the run, and its message, in place.
  readonly cancelPosted: Promise<boolean>;

  constructor(message: string, cancelPosted: Promise<boolean>) {
    super(message);
    this.cancelPosted = cancelPosted;
  }
}

// Messages on their way to chat-stream-init, by session.  Until the server
// takes one, its session still reads as the turn before, so a panel mounted
// meanwhile (the sending one unmounted) can't tell a turn is starting.
const sendsInFlight = new Map<number, Promise<void>>();

// So a reopened chat refreshes what tools finished while its panel was
// closed. Lost on reload.
const unfollowedTurns = new Map<number, TrackedTurn>();
// Sessions the user never reopens would otherwise pile up.
const MAX_UNFOLLOWED_TURNS = 20;

function leaveUnfollowed(turn: TrackedTurn): void {
  unfollowedTurns.delete(turn.sessionId);
  unfollowedTurns.set(turn.sessionId, turn);
  if (unfollowedTurns.size > MAX_UNFOLLOWED_TURNS) {
    const oldest = unfollowedTurns.keys().next().value;
    if (oldest !== undefined) unfollowedTurns.delete(oldest);
  }
}

export function messagePosted(sessionId: number): Promise<void> {
  return sendsInFlight.get(sessionId) ?? Promise.resolve();
}

export function postingMessage(sessionId: number): boolean {
  return sendsInFlight.has(sessionId);
}

/**
 * Composable for AI Agent SSE streaming.
 *
 * Strategy: SSE + parallel polling that never stops.  SSE gives
 * real-time tool/content updates when the proxy chain cooperates.
 * Polling runs alongside for the entire duration — it watches for a
 * new *final* assistant message (one with content and no tool calls)
 * so it isn't fooled by intermediate assistant messages saved during
 * tool execution.  Whichever delivery path completes first wins.
 *
 * Polling is never cancelled when SSE events arrive because proxy
 * layers on staging/production can buffer events after the initial
 * handshake, leaving the UI stuck if polling was already stopped.
 */

function _log(...args: unknown[]) {
  if (import.meta.env.DEV) {
    console.warn("[AIStream]", new Date().toISOString().slice(11, 23), ...args);
  }
}

export function useAIAgentStream(sessionId: Ref<number | null>) {
  const queryClient = useQueryClient();
  const state = ref<StreamState>({
    isStreaming: false,
    currentMessage: "",
    toolCalls: [],
    error: null,
    tokensUsed: 0,
    iteration: 0,
  });

  let eventSource: EventSource | null = null;
  let pollIntervalId: ReturnType<typeof setInterval> | null = null;
  // Tracks whether the promise has already been settled (by SSE or poll)
  // to avoid double-resolve/reject.  The current turn's: see `turnSeq`.
  let settled = false;
  // Numbers the turns followTurn starts.  `settled` is the newest turn's, so
  // an older turn's late callbacks compare their number before touching it.
  let turnSeq = 0;
  // Stored so cancelSession() can settle the sendMessageStreaming() promise.
  let activeReject: ((reason: Error) => void) | null = null;
  let disposed = false;
  let following: TrackedTurn | null = null;
  let runHasMessage = false;
  // Ends the current turn unless its run has the message, once its panel has
  // unmounted with the stream still open.
  let endDetachedTurn: (() => void) | null = null;
  let detachedTimer: ReturnType<typeof setTimeout> | null = null;
  // The turn id a Stop sends. A resumed turn keeps the id from the read that
  // found it running.
  let followedTurnId: Promise<string | null> | string | null = null;
  let finishing: {
    turn: TrackedTurn;
    intervalId: ReturnType<typeof setInterval>;
  } | null = null;

  /**
   * Send a message and stream the response.
   *
   * Resolves with a {@link StreamResult} so callers know whether polling
   * already pushed fresh messages into the TanStack query cache.  When
   * `messagesAlreadyCached` is true, an immediate `refetch()` should be
   * avoided — it can overwrite the cached data with a stale API response
   * on production (proxy caching, timing, etc.).
   */
  function sendMessageStreaming(
    message: string,
    context?: AIAgentContext,
    attachments?: ChatAttachment[],
    effort?: ReasoningEffort,
  ): Promise<StreamResult> {
    return followTurn({ message, context, attachments, effort });
  }

  /**
   * Follow a turn this instance did not start: the panel was remounted, or
   * the page reloaded, while the agent was still working.  That turn's SSE
   * stream closed with the instance that opened it, so this polls alone.
   * Resolves like {@link sendMessageStreaming}.
   */
  function resumeStreaming(
    turnId: string | null = null,
  ): Promise<StreamResult> {
    // Nothing would ever stop its polling.
    if (disposed) return Promise.reject(new Error("The chat has closed"));
    return followTurn(null, turnId);
  }

  async function readRunState(sid: number): Promise<AIAgentRunState> {
    const { data } = await api.get(`/ai-agents/sessions/${sid}/run-state/`);
    return data as AIAgentRunState;
  }

  function cacheRunState(sid: number, runState: AIAgentRunState): void {
    queryClient.setQueryData<AIAgentSession>(
      ["ai_agent", "session", sid],
      (session) => session && { ...session, ...runState },
    );
  }

  function refreshUnfollowedTurn(
    messages: AIAgentMessage[],
    runState?: Pick<AIAgentSession, "runAlive" | "runFinishing">,
  ): void {
    const turn =
      sessionId.value === null
        ? undefined
        : unfollowedTurns.get(sessionId.value);
    if (!turn) return;
    unfollowedTurns.delete(turn.sessionId);
    const running = refreshFinishedToolCalls(
      queryClient,
      turn,
      turnToolCalls(turnAfter(messages, turn.since)),
    );
    // A run that may still hold its lease can start another tool call.
    if (running > 0 || runMayStillSave(runState) !== false) {
      refreshAsToolsFinish(turn);
    }
  }

  /**
   * A tool already running when a cancel lands still finishes, and saves
   * its change after the cancel's refresh.
   */
  function refreshAsToolsFinish(turn: TrackedTurn): void {
    // Unmounted, nothing would stop its polling: the next panel follows.
    if (disposed) {
      leaveUnfollowed(turn);
      return;
    }
    stopFinishing();
    const start = Date.now();
    let inFlight = false;
    let missedLeases = 0;
    let unknownReads = 0;
    // A stale check must not stop a newer follow-up.
    function stopOwn(): void {
      clearInterval(intervalId);
      if (finishing?.intervalId === intervalId) finishing = null;
    }

    async function check(): Promise<void> {
      const { data } = await api.get(
        `/ai-agents/sessions/${turn.sessionId}/messages/`,
      );
      const running = refreshFinishedToolCalls(
        queryClient,
        turn,
        turnToolCalls(turnAfter(data as AIAgentMessage[], turn.since)),
      );
      // Until the run is gone, since one holding its lease can start another
      // call; an unknown lease counts as gone once nothing is running.
      const alive = runMayStillSave(await readRunState(turn.sessionId));
      const missed = alive === false || (alive === null && running === 0);
      missedLeases = missed ? missedLeases + 1 : 0;
      const unknown = alive === null;
      unknownReads = unknown ? unknownReads + 1 : 0;
      if (
        missedLeases >= LEASE_MISSES_BEFORE_GONE ||
        unknownReads >= UNKNOWN_LEASE_FOLLOW_MAX
      ) {
        stopOwn();
      }
    }

    const intervalId = setInterval(() => {
      if (inFlight) return;
      if (Date.now() - start > POLL_MAX_DURATION_MS) {
        stopOwn();
        return;
      }
      inFlight = true;
      void check()
        .catch((error: unknown) => {
          // Deleted, or the chat turned off: nothing more to follow.
          const status = axios.isAxiosError(error)
            ? error.response?.status
            : undefined;
          if (status === 403 || status === 404) stopOwn();
        })
        .finally(() => {
          inFlight = false;
        });
    }, POLL_INTERVAL_MS);
    finishing = { turn, intervalId };
  }

  function stopFinishing(): void {
    if (finishing) {
      clearInterval(finishing.intervalId);
      finishing = null;
    }
  }

  async function followTurn(
    outgoing: OutgoingMessage | null,
    resumedTurnId: string | null = null,
  ): Promise<StreamResult> {
    if (!sessionId.value) {
      throw new Error("No session ID available");
    }
    const sid = sessionId.value;
    // Stopped tools can save changes after the next turn starts.
    const stopped = outgoing ? finishing?.turn : undefined;
    if (outgoing) stopFinishing();
    const turnNo = ++turnSeq;
    function over(): boolean {
      return settled || turnNo !== turnSeq;
    }

    // Reset state
    state.value = {
      isStreaming: true,
      currentMessage: "",
      toolCalls: [],
      error: null,
      tokensUsed: 0,
      iteration: 0,
    };
    settled = false;
    runHasMessage = false;
    followedTurnId = resumedTurnId;

    // Set by the polling path when it pushes messages into the query
    // cache before resolving.  Exposed via the returned StreamResult.
    let messagesAlreadyCached = false;

    return new Promise<StreamResult>((resolve, reject) => {
      activeReject = reject;
      (async () => {
        try {
          let requestId: string | null = null;
          if (outgoing) {
            const init = api.post(
              `/ai-agents/sessions/${sid}/chat-stream-init/`,
              outgoing,
            );
            followedTurnId = init.then(
              ({ data }) => (typeof data.turn === "string" ? data.turn : null),
              () => null,
            );
            const posted = init.then(
              () => undefined,
              () => undefined,
            );
            sendsInFlight.set(sid, posted);
            void posted.then(() => {
              if (sendsInFlight.get(sid) === posted) sendsInFlight.delete(sid);
            });
            const initResponse = await init;

            if (over()) return;

            if (initResponse.data.status !== "initialized") {
              throw new Error("Failed to initialize stream");
            }

            requestId =
              initResponse.data.requestId || initResponse.data.request_id;
            if (!requestId) {
              throw new Error(
                "No request ID returned from stream initialization",
              );
            }
          }

          // Snapshot the last final assistant message ID so polling can
          // detect when the backend produces a *new* response.  A sent turn
          // reads the API as well: another tab's answer may be missing from
          // the cache.
          const cached =
            queryClient.getQueryData<AIAgentMessage[]>([
              "ai_agent",
              "messages",
              sessionId.value,
            ]) ?? [];
          let lastFinalIdBefore: number | null = lastFinalAssistantId(cached);
          if (cached.length === 0 || outgoing) {
            try {
              const { data } = await api.get(
                `/ai-agents/sessions/${sessionId.value}/messages/`,
              );
              const fresh = lastFinalAssistantId(data as AIAgentMessage[]);
              // Ids grow, and a proxy can serve an older list than the cache.
              if (
                fresh !== null &&
                (lastFinalIdBefore === null || fresh > lastFinalIdBefore)
              ) {
                lastFinalIdBefore = fresh;
              }
            } catch {
              // The cache's, or none.
            }
          }
          if (over()) return;
          // The cache may already hold a left turn's answer, with tool
          // results before it still unrefreshed.
          const left = outgoing ? undefined : unfollowedTurns.get(sid);
          unfollowedTurns.delete(sid);
          const turn: TrackedTurn = left ?? {
            sessionId: sid,
            since:
              stopped?.sessionId === sid ? stopped.since : lastFinalIdBefore,
            refreshedToolCalls: new Set(
              stopped?.sessionId === sid ? stopped.refreshedToolCalls : [],
            ),
          };
          // Unmounted while the turn was being set up: nothing here will
          // poll it.
          if (disposed) leaveUnfollowed(turn);
          else following = turn;
          // ── Settle helpers ────────────────────────────────────
          let toolsStillRunning = 0;
          // A tool this turn follows can save its result after the turn
          // ends: one a stopped turn left running, handed over on Send or
          // across a remount.
          function followLateTools(): void {
            if (stopped?.sessionId === sid || toolsStillRunning > 0) {
              refreshAsToolsFinish(turn);
            }
          }

          function finish(): void {
            if (over()) {
              _log("finish() called but already settled");
              return;
            }
            _log("finish()", { messagesAlreadyCached, sseActive });
            settled = true;
            activeReject = null;
            following = null;
            followLateTools();
            state.value.isStreaming = false;
            cleanup();
            resolve({ messagesAlreadyCached });
          }

          function fail(error: Error): void {
            if (over()) {
              _log("fail() called but already settled:", error.message);
              return;
            }
            _log("fail()", error.message);
            settled = true;
            activeReject = null;
            following = null;
            followLateTools();
            state.value.error = error.message;
            state.value.isStreaming = false;
            cleanup();
            reject(error);
          }

          // When SSE delivers events, let it handle tool-call state.
          // Polling keeps running alongside as a completion safety net
          // because proxy layers on staging/production can buffer SSE
          // events after the initial message_start gets through.
          let sseActive = false;
          let lastSseEventAt = 0;

          // ── SSE connection ────────────────────────────────────
          function openEventSource(requestId: string): void {
            const baseUrl = api.defaults.baseURL || "";
            const streamUrl = `${baseUrl}${
              baseUrl.endsWith("/") ? "" : "/"
            }ai-agents/sessions/${sessionId.value}/chat-stream/?request_id=${encodeURIComponent(requestId)}`;

            function safeParse(
              raw: string,
              eventType: string,
            ): Record<string, unknown> | null {
              try {
                return JSON.parse(raw) as Record<string, unknown>;
              } catch {
                console.error(`[SSE] Failed to parse ${eventType} data:`, raw);
                fail(new Error(`Invalid ${eventType} data from server`));
                return null;
              }
            }

            eventSource = new EventSource(streamUrl, {
              withCredentials: true,
            });

            // Number of tool calls already tracked by polling before SSE
            // takes over.  SSE replays all events for the current message
            // from the start, so we skip the first N tool_call_start events
            // that polling already derived.  This is count-based (not
            // name-based) because the same tool can be called multiple
            // times.  The count can be off by one if a tool completes
            // between the last poll and SSE activation, but this is a
            // narrow window and the worst case is a briefly duplicated
            // entry in the UI.
            let toolCallsFromPoll = 0;

            function onSSEEvent(): void {
              lastSseEventAt = Date.now();
              if (!sseActive) {
                sseActive = true;
                toolCallsFromPoll = state.value.toolCalls.length;
              }
            }

            eventSource.addEventListener("message_start", (e) => {
              _log("SSE: message_start");
              onSSEEvent();
              // The stream opens with an empty one; the run sends its own
              // once it has saved the message.
              const data = safeParse(e.data, "message_start");
              if (!data?.session_id) return;
              runHasMessage = true;
              // Unmounted, nothing else would settle the send.
              if (disposed) finish();
            });

            eventSource.addEventListener("thinking", (e) => {
              onSSEEvent();
              const data = safeParse(e.data, "thinking");
              if (!data) return;
              _log("SSE: thinking", data.iteration);
              state.value.iteration = (data.iteration as number) || 0;
            });

            eventSource.addEventListener("content", (e) => {
              onSSEEvent();
              const data = safeParse(e.data, "content");
              if (!data) return;
              const content = (data.content as string) || "";
              _log(
                "SSE: content",
                content.length,
                "chars, preview:",
                content.slice(0, 80),
              );
              state.value.currentMessage = content;
            });

            eventSource.addEventListener("tool_call_start", (e) => {
              onSSEEvent();
              const data = safeParse(e.data, "tool_call_start");
              if (!data) return;
              _log("SSE: tool_call_start", data.tool_name);
              // Skip if polling already tracked this tool call
              if (toolCallsFromPoll > 0) {
                toolCallsFromPoll--;
                return;
              }
              state.value.toolCalls.push({
                name: data.tool_name as string,
                status: "running",
              });
            });

            eventSource.addEventListener("tool_call_complete", (e) => {
              onSSEEvent();
              const data = safeParse(e.data, "tool_call_complete");
              if (!data) return;
              const toolCall = state.value.toolCalls.find(
                (tc) => tc.name === data.tool_name && tc.status === "running",
              );
              if (toolCall) {
                toolCall.status = "complete";
                toolCall.duration = data.duration_ms as number;
              }

              // Kept even for a failed call, which polling can't tell failed.
              const callId =
                typeof data.tool_call_id === "string"
                  ? data.tool_call_id
                  : null;
              if (callId !== null) {
                if (turn.refreshedToolCalls.has(callId)) return;
                turn.refreshedToolCalls.add(callId);
              }
              if (data.success) {
                invalidateForToolNames(queryClient, [data.tool_name as string]);
              }
            });

            eventSource.addEventListener("complete", (e) => {
              onSSEEvent();
              const data = safeParse(e.data, "complete");
              if (!data) return;
              _log("SSE: complete", { tokens: data.tokens_used });
              state.value.tokensUsed = (data.tokens_used as number) || 0;
              // Sent once every tool result is saved.
              toolsStillRunning = 0;
              finish();
            });

            eventSource.addEventListener("error", (e) => {
              if (over()) return;
              // Distinguish a real server-sent error event (MessageEvent with
              // data) from a browser connection-drop event (plain Event without
              // data).  Connection drops are handled by onerror which falls
              // back to polling — surfacing them here would show a misleading
              // "An error occurred" to the user.
              const messageEvent = e as MessageEvent;
              if (typeof messageEvent.data !== "string" || !messageEvent.data) {
                return;
              }
              onSSEEvent();
              let errorMessage = "An error occurred";
              try {
                const data = JSON.parse(messageEvent.data);
                // The browser reconnected a stream whose run had already
                // taken the message, even if a buffering proxy never let an
                // event through: the turn goes on, and polling follows it.
                // Any other refusal means no run has the message.
                if (data.code === "message_taken") {
                  eventSource?.close();
                  eventSource = null;
                  sseActive = false;
                  if (!pollIntervalId) startPolling();
                  return;
                }
                errorMessage = data.error || errorMessage;
              } catch {
                errorMessage = messageEvent.data;
              }
              console.error(
                "[SSE] Received error event from server:",
                errorMessage,
              );
              fail(new Error(errorMessage));
            });

            eventSource.onerror = (evt) => {
              _log("SSE: onerror", {
                settled,
                readyState: eventSource?.readyState,
                type: (evt as Event).type,
              });
              if (over()) return;

              // SSE dropped — let polling take over tool-call state
              // updates and restart it if needed.
              sseActive = false;
              // The run has the message: a reconnect would only be told so.
              // Before then, the reconnect is what delivers it.
              if (runHasMessage) {
                eventSource?.close();
                eventSource = null;
              }
              if (!pollIntervalId) {
                startPolling();
              }
            };
          }

          // A resumed turn has no stream of its own: it closed with the
          // instance that sent the message.  Opened even if this instance's
          // panel unmounted during init: the stream request starts the run.
          if (requestId !== null) {
            openEventSource(requestId);
            // Failing lets the sending panel restore the message if no run
            // saved it.
            endDetachedTurn = () => {
              if (!runHasMessage) fail(new Error("The chat closed"));
            };
            if (disposed) endDetachedTurnLater();
          }

          // ── Parallel polling safety net ────────────────────────
          const pollStart = Date.now();
          // Tracks when we first saw session.status === "completed"
          // without a new final message.  After a grace period we
          // resolve anyway — the agent is done.
          let completedSince: number | null = null;
          const COMPLETED_GRACE_MS = 15_000;
          let missedLeases = 0;
          let quietPolls = 0;

          let pollInFlight = false;

          function startPolling(): void {
            // An unmounted panel's polling would outlive it: nothing calls
            // cleanup() again.  A panel mounted since follows the turn.
            if (pollIntervalId || disposed) return;
            pollIntervalId = setInterval(async () => {
              if (over() || pollInFlight) return;
              if (Date.now() - pollStart > POLL_MAX_DURATION_MS) {
                fail(new Error("Response timeout"));
                return;
              }

              pollInFlight = true;
              try {
                const { data } = await api.get(
                  `/ai-agents/sessions/${sid}/messages/`,
                );
                if (over()) {
                  _log("POLL: got response but already settled");
                  return;
                }

                const messages = data as AIAgentMessage[];
                const newLastId = lastFinalAssistantId(messages);
                _log("POLL: messages", {
                  count: messages.length,
                  newLastId,
                  lastFinalIdBefore,
                  sseActive,
                });

                // A resumed turn has no pending message of its own on
                // screen: keep the cache current so the user's message
                // shows as soon as the run saves it.
                if (!outgoing) {
                  queryClient.setQueryData(
                    ["ai_agent", "messages", sid],
                    messages,
                  );
                }

                const currentTurn = turnAfter(messages, lastFinalIdBefore);
                const toolCalls = turnToolCalls(currentTurn);

                // Refresh what each tool changed once its result is saved:
                // SSE's per-tool events may be buffered, or gone with the
                // panel that opened the stream.
                const stillRunning = refreshFinishedToolCalls(
                  queryClient,
                  turn,
                  turnToolCalls(turnAfter(messages, turn.since)),
                );
                toolsStillRunning = stillRunning;

                // Extract tool call progress and iteration count from
                // the current turn's messages only (after the baseline).
                if (!sseActive) {
                  if (toolCalls.length > 0) {
                    state.value.toolCalls = toolCalls.map((call) => ({
                      name: call.name,
                      status: call.done ? "complete" : "running",
                    }));
                  }
                  state.value.iteration = currentTurn.filter(
                    (m) => m.role === "assistant",
                  ).length;
                }

                // A new final assistant message appeared
                if (newLastId !== null && newLastId !== lastFinalIdBefore) {
                  _log("POLL: new final message detected, finishing");
                  messagesAlreadyCached = true;
                  queryClient.setQueryData(
                    ["ai_agent", "messages", sid],
                    messages,
                  );
                  finish();
                  return;
                }

                // Check session status for terminal states so agent
                // errors don't leave the user stuck for 10 minutes.
                try {
                  const session = await readRunState(sid);
                  if (over()) return;
                  // A tool still running, or started since the read, saves
                  // after this poll stops.
                  function handOnRunningTools(): void {
                    if (
                      stillRunning > 0 ||
                      runMayStillSave(session) !== false
                    ) {
                      refreshAsToolsFinish(turn);
                    }
                  }

                  if (session.status === "error") {
                    queryClient.setQueryData(
                      ["ai_agent", "messages", sid],
                      messages,
                    );
                    cacheRunState(sid, session);
                    handOnRunningTools();
                    fail(new Error("AI agent encountered an error"));
                    return;
                  }

                  // The run's process was killed mid-turn (a deploy, a
                  // crash), so it never recorded a terminal status and no
                  // answer is coming.
                  const leaseMissing =
                    session.status === "active" &&
                    session.runAlive === false &&
                    (!sseActive ||
                      Date.now() - lastSseEventAt > SSE_PROOF_OF_LIFE_MS);
                  missedLeases = leaseMissing ? missedLeases + 1 : 0;
                  // Nothing saved since the last answer: its run died before
                  // saving the message. An unknown lease proves nothing: let
                  // go then, and a resend could start a second run beside a
                  // live one.
                  if (
                    !outgoing &&
                    session.status === "active" &&
                    currentTurn.length === 0
                  ) {
                    quietPolls =
                      session.runAlive === false ? quietPolls + 1 : 0;
                    if (quietPolls >= LEASE_MISSES_BEFORE_GONE) {
                      cacheRunState(sid, session);
                      fail(new Error(RUN_STOPPED_MESSAGE));
                      return;
                    }
                  }
                  if (missedLeases >= LEASE_MISSES_BEFORE_GONE) {
                    queryClient.setQueryData(
                      ["ai_agent", "messages", sid],
                      messages,
                    );
                    cacheRunState(sid, session);
                    fail(new Error(RUN_STOPPED_MESSAGE));
                    return;
                  }

                  if (session.status === "completed") {
                    // Agent finished but no new final message yet —
                    // e.g. hit max iterations, last msg was a tool call.
                    // Give a grace period, then resolve with what we have.
                    completedSince ??= Date.now();
                    if (Date.now() - completedSince > COMPLETED_GRACE_MS) {
                      queryClient.setQueryData(
                        ["ai_agent", "messages", sid],
                        messages,
                      );
                      cacheRunState(sid, session);
                      handOnRunningTools();
                      finish();
                      return;
                    }
                  } else if (session.status !== "active") {
                    // Other terminal states (completed, archived)
                    cacheRunState(sid, session);
                    handOnRunningTools();
                    finish();
                    return;
                  }
                } catch {
                  // Non-critical — keep polling
                }
              } catch {
                // Network blip — keep trying
              } finally {
                pollInFlight = false;
              }
            }, POLL_INTERVAL_MS);
          }

          // Start polling immediately alongside SSE
          startPolling();
        } catch (error) {
          // A newer turn owns the state, the stream and the polling now.
          if (turnNo !== turnSeq) {
            reject(error);
            return;
          }
          state.value.error = getApiErrorMessage(error);
          state.value.isStreaming = false;
          cleanup();
          if (stopped?.sessionId === sid) refreshAsToolsFinish(stopped);
          reject(error);
        }
      })();
    });
  }

  function endDetachedTurnLater(): void {
    if (!endDetachedTurn || detachedTimer) return;
    detachedTimer = setTimeout(endDetachedTurn, DETACHED_STREAM_MAX_MS);
  }

  function cleanup() {
    if (detachedTimer) {
      clearTimeout(detachedTimer);
      detachedTimer = null;
    }
    if (pollIntervalId) {
      clearInterval(pollIntervalId);
      pollIntervalId = null;
    }
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
  }

  /**
   * Cancel the current streaming session.
   */
  async function cancelSession(): Promise<void> {
    if (!sessionId.value) {
      throw new Error("No session ID available");
    }

    // Update state IMMEDIATELY to stop the thinking indicator
    state.value.isStreaming = false;
    state.value.currentMessage = "";
    state.value.error = "Cancelling session...";

    // An unmount during the cancel must keep late-tool tracking.
    if (following) refreshAsToolsFinish(following);
    following = null;
    // What the agent changed before the cancel stays changed, whether or not
    // the cancel request goes through.
    invalidateForToolNames(
      queryClient,
      state.value.toolCalls.map((call) => call.name),
    );
    const sid = sessionId.value;
    const turnId = followedTurnId;
    // After any message still being posted: a cancel landing first would find
    // nothing to stop, and the post would then leave the session active with
    // no run.
    const cancelRequest = messagePosted(sid)
      .then(async () =>
        api.post(`/ai-agents/sessions/${sid}/cancel/`, { turn: await turnId }),
      )
      .then((response) => {
        // Nothing stopped: the chat reads the session again, which names the
        // turn for the next Stop.
        if (response.data.status === "turn_unknown") {
          throw new StopNotPlacedError(response.data.message);
        }
        return response;
      });
    // Settle the sendMessageStreaming() promise so callers don't hang.
    if (!settled && activeReject) {
      settled = true;
      activeReject(
        new TurnCancelledError(
          "Session cancelled",
          cancelRequest.then(
            () => true,
            () => false,
          ),
        ),
      );
      activeReject = null;
    } else {
      settled = true;
    }

    // A turn sent while the cancel is in flight owns `state`: leave it alone.
    const turnNo = turnSeq;
    try {
      cleanup();

      const response = await cancelRequest;

      if (turnNo !== turnSeq) return;
      if (response.data.status === "already_finished") {
        state.value.error = null;
      } else {
        state.value.error = "Session cancelled";
      }
    } catch (error: unknown) {
      console.error("Failed to cancel session:", error);
      if (turnNo === turnSeq) {
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        state.value.error =
          error instanceof StopNotPlacedError
            ? error.message
            : axiosError?.response?.data?.message || "Failed to cancel session";
      }
      throw error;
    }
  }

  tryOnScopeDispose(() => {
    disposed = true;
    if (following) leaveUnfollowed(following);
    following = null;
    if (finishing) leaveUnfollowed(finishing.turn);
    stopFinishing();
    if (pollIntervalId) {
      clearInterval(pollIntervalId);
      pollIntervalId = null;
    }
    // The stream request is what starts the run.  Closed before the run has
    // saved the message, the request can be dropped, and the message with it.
    if (!eventSource || runHasMessage) {
      cleanup();
    } else {
      endDetachedTurnLater();
    }
  });

  return {
    state,
    sendMessageStreaming,
    resumeStreaming,
    refreshUnfollowedTurn,
    cancelSession,
    cleanup,
  };
}
